import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';

const getAuthHeaders = (token) => ({
    headers: { Authorization: `Bearer ${token}` }
});

/**
 * Downloads a video file with proper authentication
 * @param {string} token - The user's authentication token
 * @param {string} filename - The name of the video file to download
 * @returns {Promise<Blob>} - A promise that resolves to the video blob
 */
export const downloadVideo = async (token, filename) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/video/download/${filename}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            responseType: 'blob',
        });
        return response.data;
    } catch (error) {
        console.error('Error downloading video:', error);
        throw error;
    }
};

/**
 * Gets a video stream URL with authentication for viewing
 * @param {string} token - The user's authentication token
 * @param {string} filename - The name of the video file to view
 * @returns {Promise<Blob>} - A promise that resolves to the video blob for viewing
 */
export const viewVideo = async (token, filename) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/video/view/${filename}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            responseType: 'blob',
        });
        return response.data;
    } catch (error) {
        console.error('Error viewing video:', error);
        throw error;
    }
};

/**
 * Creates a blob URL for video viewing with authentication
 * @param {string} token - The user's authentication token
 * @param {string} filename - The name of the video file
 * @returns {Promise<string>} - A promise that resolves to a blob URL
 */
export const createVideoViewUrl = async (token, filename) => {
    try {
        const videoBlob = await viewVideo(token, filename);
        return URL.createObjectURL(videoBlob);
    } catch (error) {
        console.error('Error creating video view URL:', error);
        throw error;
    }
};

/**
 * Downloads a video file and triggers browser download
 * @param {string} token - The user's authentication token
 * @param {string} filename - The name of the video file to download
 * @param {string} [downloadName] - Optional custom filename for download
 */
export const triggerVideoDownload = async (token, filename, downloadName = null) => {
    try {
        const videoBlob = await downloadVideo(token, filename);
        
        // Create a temporary URL for the blob
        const url = URL.createObjectURL(videoBlob);
        
        // Create a temporary anchor element and trigger download
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadName || filename;
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error triggering video download:', error);
        throw error;
    }
};

/**
 * Gets available video models and their information
 * @param {string} token - The user's authentication token
 * @returns {Promise<object>} - Available models and recommendations
 */
export const getVideoModels = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/video/models`, getAuthHeaders(token));
        return response.data;
    } catch (error) {
        console.error('Error fetching video models:', error);
        throw error;
    }
};

/**
 * Checks image-to-video availability for the user's region
 * @param {string} token - The user's authentication token
 * @returns {Promise<object>} - Availability status and workflow options
 */
export const checkImageToVideoAvailability = async (token) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/video/test-image-to-video`, getAuthHeaders(token));
        return response.data;
    } catch (error) {
        console.error('Error checking image-to-video availability:', error);
        throw error;
    }
};

/**
 * Generates a video using the updated API format
 * @param {string} token - The user's authentication token
 * @param {FormData} formData - The form data containing video generation parameters
 * @returns {Promise<object>} - Response with video_url and download_url
 */
export const generateVideo = async (token, formData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/video/generate`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                // Don't set Content-Type manually - let axios handle multipart/form-data boundary
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error generating video:', error);
        throw error;
    }
};

/**
 * Interpolates between two images to create a smooth video transition
 * @param {string} token - The user's authentication token
 * @param {FormData} formData - The form data containing interpolation parameters (first_frame, last_frame, prompt, etc.)
 * @returns {Promise<object>} - Response with video_url and download_url
 */
export const interpolateVideo = async (token, formData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/video/interpolate`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                // Don't set Content-Type manually - let axios handle multipart/form-data boundary
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error interpolating video:', error);
        throw error;
    }
};

/**
 * Extends a FRANCA AI-generated video with additional frames
 * @param {string} token - The user's authentication token
 * @param {FormData} formData - The form data containing extension parameters (video, prompt, etc.)
 * @returns {Promise<object>} - Response with video_url and download_url
 */
export const extendVideo = async (token, formData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/video/extend`, formData, {
            headers: {
                Authorization: `Bearer ${token}`,
                // Don't set Content-Type manually - let axios handle multipart/form-data boundary
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error extending video:', error);
        throw error;
    }
};

/**
 * Fetches user's video generation history
 * @param {string} token - The user's authentication token
 * @param {number} limit - Maximum number of items to return
 * @param {number} sinceMinutes - Filter history from the last N minutes
 * @returns {Promise<Array>} - Array of video generation history items
 */
export const getVideoHistory = async (token, limit = 5, sinceMinutes = 60) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (sinceMinutes) params.append('since_minutes', sinceMinutes.toString());
    
    try {
        const response = await axios.get(
            `${API_BASE_URL}/video/history?${params.toString()}`,
            getAuthHeaders(token)
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching video history:', error);
        throw error;
    }
};